/**
 * Feature flags for the FAANG-grade _app.tsx refactoring
 *
 * This file controls the gradual rollout of new features during the refactoring process.
 * All flags default to false (old behavior) and can be enabled incrementally.
 */

// Environment-based feature detection
const getEnvFlag = (key: string, defaultValue = false): boolean => {
  const value = process.env[key];
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return defaultValue;
};

// Feature flag definitions
export const FEATURES = {
  // Phase 1: Preparation (currently active)
  TESTING_INFRASTRUCTURE: getEnvFlag('NEXT_PUBLIC_ENABLE_TESTING_INFRASTRUCTURE', true),

  // Phase 2: Custom Hooks Extraction
  AUTH_HYDRATION_HOOK: getEnvFlag('NEXT_PUBLIC_ENABLE_AUTH_HYDRATION_HOOK', false),
  SERVICE_WORKER_HOOK: getEnvFlag('NEXT_PUBLIC_ENABLE_SW_HOOK', false),
  OFFLINE_SYNC_HOOK: getEnvFlag('NEXT_PUBLIC_ENABLE_OFFLINE_SYNC_HOOK', false),
  TEST_HARNESS_HOOK: getEnvFlag('NEXT_PUBLIC_ENABLE_TEST_HARNESS_HOOK', false),
  ANALYTICS_SILENCER_HOOK: getEnvFlag('NEXT_PUBLIC_ENABLE_ANALYTICS_SILENCER_HOOK', false),

  // Phase 3: Provider Composition
  NEW_APP_PROVIDERS: getEnvFlag('NEXT_PUBLIC_ENABLE_NEW_APP_PROVIDERS', false),
  PROVIDER_MEMOIZATION: getEnvFlag('NEXT_PUBLIC_ENABLE_PROVIDER_MEMOIZATION', false),
  LAZY_PROVIDER_LOADING: getEnvFlag('NEXT_PUBLIC_ENABLE_LAZY_PROVIDER_LOADING', false),

  // Phase 4: Layout System
  NEW_LAYOUT_SYSTEM: getEnvFlag('NEXT_PUBLIC_ENABLE_NEW_LAYOUT_SYSTEM', false),
  AUTH_LAYOUT_COMPONENT: getEnvFlag('NEXT_PUBLIC_ENABLE_AUTH_LAYOUT_COMPONENT', false),
  APP_LAYOUT_COMPONENT: getEnvFlag('NEXT_PUBLIC_ENABLE_APP_LAYOUT_COMPONENT', false),
  RESPONSIVE_LAYOUTS: getEnvFlag('NEXT_PUBLIC_ENABLE_RESPONSIVE_LAYOUTS', false),

  // Phase 5: Test Harness Isolation
  TEST_HARNESS_ISOLATION: getEnvFlag('NEXT_PUBLIC_ENABLE_TEST_HARNESS_ISOLATION', false),
  PRODUCTION_OPTIMIZATION: getEnvFlag('NEXT_PUBLIC_ENABLE_PRODUCTION_OPTIMIZATION', false),

  // Phase 6: Optimization & Cleanup
  CODE_SPLITTING: getEnvFlag('NEXT_PUBLIC_ENABLE_CODE_SPLITTING', false),
  PERFORMANCE_MONITORING: getEnvFlag('NEXT_PUBLIC_ENABLE_PERFORMANCE_MONITORING', false),
  FINAL_CLEANUP: getEnvFlag('NEXT_PUBLIC_ENABLE_FINAL_CLEANUP', false),

} as const;

// Feature flag validation
export const validateFeatureFlags = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];


  if (FEATURES.NEW_LAYOUT_SYSTEM && !FEATURES.NEW_APP_PROVIDERS) {
    errors.push('NEW_LAYOUT_SYSTEM requires NEW_APP_PROVIDERS to be enabled');
  }

  if (FEATURES.TEST_HARNESS_ISOLATION && !FEATURES.TEST_HARNESS_HOOK) {
    errors.push('TEST_HARNESS_ISOLATION requires TEST_HARNESS_HOOK to be enabled');
  }

  // Phase dependency checks
  const phase2Hooks = [
    FEATURES.AUTH_HYDRATION_HOOK,
    FEATURES.SERVICE_WORKER_HOOK,
    FEATURES.OFFLINE_SYNC_HOOK,
    FEATURES.TEST_HARNESS_HOOK,
    FEATURES.ANALYTICS_SILENCER_HOOK,
  ];

  if (FEATURES.NEW_APP_PROVIDERS && !phase2Hooks.every(hook => hook)) {
    errors.push('NEW_APP_PROVIDERS requires all Phase 2 hooks to be enabled');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Runtime feature flag status
export const getFeatureFlagStatus = () => {
  const validation = validateFeatureFlags();

  return {
    flags: FEATURES,
    validation,
    enabledFeatures: Object.entries(FEATURES)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key),
    disabledFeatures: Object.entries(FEATURES)
      .filter(([, enabled]) => !enabled)
      .map(([key]) => key),
  };
};

// Development helpers
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  // Expose feature flags to browser console for debugging
  (window as any).__FEATURE_FLAGS__ = getFeatureFlagStatus();
}